import SubmissionProcessingProgress from 'components/SubmissionProcessingProgress';
import { useTranslation } from 'react-i18next';
import { Button, Divider, Icon, IconButton, Skeleton, Stack, styled, Text } from 'shell/components';

const QuizGradingPanel = ({ canManageQuiz, submissionImport }) => {
    const { t } = useTranslation('quiz');
    const disabledTooltip = t(
        'view.grading.instructor-only',
        'An instructor needs to import submissions before you can start grading.'
    );
    const {
        canImport,
        canReset,
        findingSubmissions,
        loadingBatches,
        hasSubmissions,
        isProcessing,
        resetting,
        reviewed,
        pendingReview,
        total,
        processingProcessed,
        processingTotal,
        failed,
        onImportSubmissions,
        onStartReviewing,
        onManageSubmissions,
        onResetSubmissions,
    } = submissionImport;
    const hasImportedSubmissions = hasSubmissions || total > 0 || isProcessing;

    if (loadingBatches) {
        return (
            <Stack
                component={'aside'}
                aria-label={t('view.grading.title', 'Quiz Grading and Feedback')}
                aria-busy
                spacing={4}
            >
                <Skeleton aria-hidden sx={{ fontSize: '1rem', maxWidth: '100%' }} width={325} />
                <GradingCard spacing={3} aria-hidden>
                    <Skeleton variant={'rounded'} height={36} />
                    <Stack>
                        <Skeleton sx={{ fontSize: '0.75rem' }} />
                        <Skeleton sx={{ fontSize: '0.75rem' }} />
                        <Skeleton sx={{ fontSize: '0.75rem' }} />
                        <Skeleton sx={{ fontSize: '0.75rem' }} />
                    </Stack>
                </GradingCard>
            </Stack>
        );
    }

    return (
        <Stack component={'aside'} aria-labelledby={'quiz-grading-heading'} spacing={4}>
            <Text id={'quiz-grading-heading'} component={'h2'} variant={'sh1'} weight={'semibold'}>
                {t('view.grading.title', 'Quiz Grading and Feedback')}
            </Text>
            <GradingCard spacing={4}>
                {hasImportedSubmissions ? (
                    <>
                        <StatusCard spacing={3} aria-live={'polite'}>
                            <Stack direction={'row'} alignItems={'center'} spacing={1}>
                                <Icon
                                    kind={'error'}
                                    sx={(theme) => ({
                                        fontSize: 20,
                                        p: 0.5,
                                        borderRadius: '50%',
                                        background: theme.palette.ensightful.primary[500],
                                        color: theme.palette.ensightful.shades[0],
                                    })}
                                />
                                <Text>{t('submissions.status', 'Submission Status')}</Text>
                            </Stack>
                            <Stack direction={'row'} justifyContent={'space-between'}>
                                <Text>{t('submissions.pending-review', 'Pending review')}</Text>
                                <Text color={'light'} weight={'semibold'}>
                                    {pendingReview}
                                </Text>
                            </Stack>
                            <Stack direction={'row'} justifyContent={'space-between'}>
                                <Text>{t('common:ui.completed', 'Completed')}</Text>
                                <Text color={'light'}>
                                    {reviewed} / {total}
                                </Text>
                            </Stack>
                        </StatusCard>
                        <Button disabled={isProcessing || resetting || !pendingReview} onClick={onStartReviewing}>
                            {t('submissions.start-reviewing', 'Start Reviewing')}
                        </Button>
                        <Button
                            disabled={!hasSubmissions || resetting}
                            variant={'secondary'}
                            onClick={onManageSubmissions}
                        >
                            {t('submissions.manage', 'Manage Submissions')}
                        </Button>
                        <Divider />
                        {isProcessing ? (
                            <SubmissionProcessingProgress
                                completed={processingProcessed}
                                total={processingTotal}
                                label={t('submissions.processing', '{{count}}/{{total}} quizzes', {
                                    count: processingProcessed,
                                    total: processingTotal,
                                })}
                            >
                                {/* TODO: Enable when quiz batch cancellation is implemented. */}
                                <IconButton
                                    aria-label={t('common:controls.cancel', 'Cancel')}
                                    disabled
                                    kind={'exit'}
                                    size={'small'}
                                    variant={'square'}
                                />
                            </SubmissionProcessingProgress>
                        ) : (
                            <Stack spacing={4}>
                                <Button
                                    variant={'borderless'}
                                    disabled={!canImport}
                                    loading={findingSubmissions}
                                    loadingGrow
                                    startIcon={<Icon kind={'upload'} />}
                                    onClick={onImportSubmissions}
                                >
                                    {t('submissions.import-additional', 'Import Additional Submissions')}
                                </Button>
                                {canManageQuiz && (
                                    <Button
                                        disabled={!canReset}
                                        loading={resetting}
                                        startIcon={<Icon kind={'refresh'} />}
                                        variant={'borderless'}
                                        color={'destructive'}
                                        onClick={onResetSubmissions}
                                    >
                                        {t('submissions.reset', 'Reset Submissions')}
                                    </Button>
                                )}
                            </Stack>
                        )}
                    </>
                ) : (
                    <>
                        <Button
                            disabled={!canImport}
                            loading={findingSubmissions}
                            loadingGrow={findingSubmissions}
                            startIcon={<Icon kind={'upload'} />}
                            tooltip={!canManageQuiz ? { component: disabledTooltip } : undefined}
                            onClick={onImportSubmissions}
                        >
                            {t('view.grading.import-submissions', 'Import Submissions')}
                        </Button>
                        <Text color={'light'}>
                            {t(
                                'view.grading.description',
                                'Quizzes will be graded immediately after you import. Please note, editing quiz details after upload is restricted to ensure consistent grades/feedback for all students. If you need to make changes, reset grading.'
                            )}
                        </Text>
                    </>
                )}
                {failed && (
                    <Text color={'error'} role={'alert'}>
                        {t(
                            'submissions.failed',
                            'Some submissions could not be processed. Import them again to retry.'
                        )}
                    </Text>
                )}
            </GradingCard>
        </Stack>
    );
};

const GradingCard = styled(Stack)(({ theme }) => ({
    padding: theme.spacing(5),
    borderRadius: '16px',
    background: theme.palette.ensightful.neutral[50],
}));

const StatusCard = styled(Stack)(({ theme }) => ({
    width: '100%',
    padding: theme.spacing(3),
    borderRadius: '8px',
    background: theme.palette.ensightful.shades[0],
}));

export default QuizGradingPanel;
